/* eslint-disable react/jsx-no-bind */

import React, {useState} from 'react';
import {Modal, ModalHeader, ModalBody, ModalFooter} from 'reactstrap';
import {Formik, Form} from 'formik';
import {FormattedMessage} from 'react-intl';
import {constants} from '@neosvoc/nv-platform-core-common';
import Yup from '../../../yup';
import messages from '../../../../res/translations/messages';
import FormGroupField from '../../admin/FormGroupField';
import FormGroupRadio from '../FormGroupRadio';
import EmailBodyHtmlFields from './EmailBodyHtmlFields';
import DatePicker from 'react-datepicker';
import ReactDatePickerInput from '../../quicksightDashboards/components/ReactDatePickerInput';
import 'react-datepicker/dist/react-datepicker.css';
import TooltipIcon from '../../TooltipIcon';

const SendMessageSchema = Yup.object().shape(
    {
        emailFrom: Yup.string().required().email(),
        emailFromName: Yup.string().max(32),
        emailSubject: Yup.string().required(),
        emailBodyText: Yup.string().required(),
        emailBodyHtml: Yup.string().required(),
        // emailBodyHtmlFile: Yup.required(),
        emailBodyHtmlType: Yup.string().required().oneOf(Object.values(constants.EMAIL_TEMPLATE_TYPE)),
        smsFrom: Yup.string().required().max(11).matches(constants.GSM_7BIT_ALPHABET_REGEX, 'gsmAlphabet'),
        smsBody: Yup.string().required().matches(constants.GSM_7BIT_ALPHABET_REGEX, 'gsmAlphabet'),
        enableScheduling: Yup.boolean().required(),
        dateScheduling: Yup.string().nullable(),
        enableTesting: Yup.boolean().required(),
        testEmail: Yup.string().when(
            ['testPhone', 'enableTesting'], {
                is: (testPhone, enableTesting) => enableTesting && !testPhone,
                otherwise: Yup.string(),
                then: Yup.string().required().email(),
            },
        ),
        testPhone: Yup.string().when(
            ['testEmail', 'enableTesting'], {
                is: (testEmail, enableTesting) => enableTesting && !testEmail,
                otherwise: Yup.string(),
                then: Yup.string().required(),
            },
        ),
    },
    [['testPhone', 'testEmail']],
);

const SendMessageModalButton = ({children, onSubmit, onSave, onTest, initialValues, organization, extraFields, noTrackers, ...rest}) => {
    const formRef = React.createRef();
    initialValues = initialValues || {};
    const defaultInitialValues = {
        emailFrom: initialValues.emailFrom || organization.emailFrom || '',
        emailFromName: initialValues.emailFromName || organization.emailFromName || '',
        emailSubject: initialValues.emailSubject || '',
        emailBodyText: initialValues.emailBodyText || 'Corpo del messaggio\nInizia il questionario: {{url}}',
        emailBodyHtml: initialValues.emailBodyHtml || '<p>Corpo del messaggio</p><p><a href="{{url}}">Inizia il questionario</a></p>',
        emailBodyHtmlType: initialValues.emailBodyHtmlType || constants.EMAIL_TEMPLATE_TYPE.DEFAULT,
        emailBodyHtmlFile: null,
        smsFrom: initialValues.smsFrom || organization.smsFrom || '',
        smsBody: initialValues.smsBody || 'Inizia il questionario: {{url}}',
        enableScheduling: initialValues.enableScheduling || false,
        dateScheduling: initialValues.dateScheduling ? new Date(initialValues.dateScheduling) : null,
        enableTesting: initialValues.enableTesting || false,
        enableSending: initialValues.enableSending || true,
        sendType: initialValues.sendType || '3',
        testEmail: initialValues.testEmail || '',
        testPhone: initialValues.testPhone || '',
        extraFields: extraFields || [],
    };
    // if there are no trackers, we disable scheduling and sending options, so the only available option is testing
    if (noTrackers) {
        defaultInitialValues.sendType = '2';
    }
    const [open, setOpen] = useState(false);
    const toggle = () => setOpen(!open);
    const toggleAndSubmit = (values) => {
        toggle();
        onSubmit(values);
    };
    const toggleAndSave = async () => {
        if (formRef.current) {
            const isValid = await SendMessageSchema.isValid(formRef.current.state.values);
            if (isValid) {
                onSave(formRef.current.state.values);
                toggle();
            }
        }
    };
    const toggleAndTest = async () => {
        if (formRef.current) {
            formRef.current.setTouched({testEmail: true, testPhone: true, smsFrom: true, emailFrom: true, emailSubject: true}, true);
            const isValid = await SendMessageSchema.isValid(formRef.current.state.values);
            if (isValid) {
                onTest(formRef.current.state.values);
                toggle();
            }
        }
    };
    const setDateScheduling = (date) => {
        if (formRef.current) {
            formRef.current.setFieldValue('dateScheduling', date);
        }
    };
    const updateOptions = (sendType) => {
        if (formRef.current) {
            formRef.current.setFieldValue('sendType', sendType);
            formRef.current.setFieldValue('enableScheduling', sendType == 1);
            formRef.current.setFieldValue('enableTesting', sendType == 2);
            formRef.current.setFieldValue('enableSending', sendType == 3);
        }
    };
    const syncScheduling = (e) => {
        updateOptions(e.currentTarget.value);
    };
    const syncSending = (e) => {
        updateOptions(e.currentTarget.value);
    };
    const syncTesting = (e) => {
        updateOptions(e.currentTarget.value);
        if (formRef.current) {
            // the value is still the previous one
            if (formRef.current.state.values.enableTesting == false) {
                formRef.current.setTouched({testEmail: true, testPhone: true}, true);
            }
        }
    };
    return (
        <button type="button" onClick={() => setOpen(true)} {...rest}>
            {children}
            <Modal isOpen={open} toggle={toggle} size="lg" className="trackers_send-message-modal">
                <ModalHeader toggle={toggle}>
                    <FormattedMessage {...messages.trackers.sendModal.title} />
                </ModalHeader>
                <Formik ref={formRef} initialValues={defaultInitialValues} onSubmit={toggleAndSubmit} validationSchema={SendMessageSchema}>
                    {({values}) => (
                        <Form>
                            <ModalBody>
                                <h6><FormattedMessage {...messages.trackers.sendModal.form.emailHeader} /></h6>
                                <div className="row">
                                    <div className="col-md-6">
                                        <FormGroupField type="email" id="smmb-email_from" name="emailFrom"
                                            label={messages.trackers.sendModal.form.emailFrom} />
                                    </div>
                                    <div className="col-md-6">
                                        <FormGroupField type="text" id="smmb-email_from_name" name="emailFromName"
                                            label={messages.trackers.sendModal.form.emailFromName} maxLength="32" />
                                    </div>
                                </div>
                                <FormGroupField type="text" id="smmb-email_subject" name="emailSubject"
                                    label={messages.trackers.sendModal.form.emailSubject} />
                                <FormGroupField component="textarea" id="smmb-email_body_text" name="emailBodyText"
                                    label={messages.trackers.sendModal.form.emailBodyText} />

                                <EmailBodyHtmlFields values={values} template={initialValues} />

                                <h6><FormattedMessage {...messages.trackers.sendModal.form.smsHeader} /></h6>
                                <FormGroupField type="text" id="smmb-from-sms" name="smsFrom" maxLength="11"
                                    label={messages.trackers.sendModal.form.smsFrom} />
                                <FormGroupField component="textarea" id="smmb-sms_body" name="smsBody"
                                    label={messages.trackers.sendModal.form.smsBody} />

                                <FormGroupRadio id="smmb-enableTesting" name="sendType" onChange={syncTesting}
                                    value="2" label={messages.trackers.sendModal.form.enableTesting} />
                                {values.sendType == '2' && (
                                    <>
                                        <FormGroupField type="text" id="smmb-testEmail" name="testEmail"
                                            label={messages.trackers.sendModal.form.testEmail} />
                                        <FormGroupField type="text" id="smmb-testPhone" name="testPhone"
                                            label={messages.trackers.sendModal.form.testPhone} />
                                        <div className="text-center" />
                                    </>
                                )}

                                {!noTrackers && (<FormGroupRadio id="smmb-enableScheduling" name="sendType" onChange={syncScheduling}
                                    value="1" label={messages.trackers.sendModal.form.enableScheduling} />
                                )}
                                {values.sendType == '1' && (
                                    <div className="row mb-10">
                                        <div className="col-md-6">
                                            <DatePicker
                                                selected={values.dateScheduling}
                                                onChange={setDateScheduling}
                                                selectsStart
                                                customInput={<ReactDatePickerInput />}
                                                dateFormat="dd/MM/yyyy HH:mm"
                                                showTimeSelect
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                )}

                                {!noTrackers && (<FormGroupRadio id="smmb-enableSend" name="sendType" onChange={syncSending}
                                    value="3" label={messages.trackers.sendModal.form.enableSending} />
                                )}

                            </ModalBody>
                            <ModalFooter>
                                <button className="btn btn-outline-secondary mr-auto" type="button" onClick={toggle}>
                                    <FormattedMessage {...messages.buttons.cancel} />
                                </button>
                                <button className="btn btn-outline-primary ml-auto" type="button" onClick={toggleAndSave}>
                                    <FormattedMessage {...messages.buttons.saveAsDraft} />
                                </button>
                                {(values.sendType == '3') && (
                                    <button className="btn btn-primary" type="submit">
                                        <FormattedMessage {...messages.trackers.sendModal.buttons.sendNow} />
                                    </button>
                                )}
                                {values.sendType == '2' && (
                                    <button className="btn btn-primary" type="button" onClick={toggleAndTest}>
                                        <FormattedMessage {...messages.trackers.sendModal.buttons.sendTest} />
                                        &nbsp;
                                        <TooltipIcon message={messages.trackers.sendModal.buttons.sendTestTooltip} />
                                    </button>
                                )}
                                {(values.sendType == '1') && (
                                    <button className="btn btn-primary" type="submit">
                                        <FormattedMessage {...messages.trackers.sendModal.buttons.sendScheduled} />
                                    </button>
                                )}

                            </ModalFooter>
                        </Form>
                    )}
                </Formik>
            </Modal>
        </button>
    );
};

export default SendMessageModalButton;
